import * as React from 'react';
import { useRefinementList, useMenu } from 'react-instantsearch';
import { IsRefinableItem } from '../helpers';

function CustomRefinementList({attribute, initialFacetValues}) {
  const {items: allItems, refine} = useRefinementList({attribute});
  const allItemsRef = React.useRef(new Map());
  const initializedRef = React.useRef(false);
  const refinableItems = allItems.map(item => item.value);

  // Initialize with all facet values from bootstrap on first render
  React.useEffect(() => {
    if (initialFacetValues && initialFacetValues.length > 0 && !initializedRef.current) {
      initialFacetValues.forEach(facetValue => {
        allItemsRef.current.set(facetValue.value, {
          value: facetValue.value,
          label: facetValue.value,
          count: facetValue.count,
          isRefined: false,
        });
      });
      initializedRef.current = true;
    }
  }, [initialFacetValues]);

  // Update items with current state from InstantSearch
  allItems.forEach(item => {
    allItemsRef.current.set(item.value, item);
  });

  const handleClick = (value) => refine(value);

  return (
    <ul>
      {Array.from(allItemsRef.current.values())
        .sort((a, b) => a.value.localeCompare(b.value))
        .map(item => {
          const isRefinable = IsRefinableItem(refinableItems, item);

          return (
            <li key={item.value} className={!isRefinable ? 'facet-empty' : 'with-value'}>
              <label>
                <input
                  type="checkbox"
                  checked={item.isRefined && item.count > 0}
                  data-checked={item.isRefined && item.count > 0}
                  onChange={() => handleClick(item.value)}
                  disabled={!isRefinable}
                />
                <span>{item.label}</span>
                <span className='ais-Menu-count'> ({item.count})</span>
              </label>
            </li>
          );
        })}
    </ul>
  );
}

function CustomMenu({attribute, initialFacetValues}) {
  const {items, refine} = useMenu({attribute});
  const allItemsRef = React.useRef(new Map());
  const initializedRef = React.useRef(false);

  const refinableItems = items.map(item => item.value);

  // Initialize with all facet values from bootstrap on first render
  React.useEffect(() => {
    if (initialFacetValues && initialFacetValues.length > 0 && !initializedRef.current) {
      initialFacetValues.forEach(facetValue => {
        allItemsRef.current.set(facetValue.value, {
          value: facetValue.value,
          label: facetValue.value,
          count: facetValue.count,
          isRefined: false,
        });
      });
      initializedRef.current = true;
    }
  }, [initialFacetValues]);

  // Update items with current state from InstantSearch
  items.forEach(item => allItemsRef.current.set(item.value, item));

  const handleClick = (value) => refine(value);

  return (
    <ul>
      {Array.from(allItemsRef.current.values())
        .sort((a, b) => a.value.localeCompare(b.value))
        .map(item => {
          const isRefinable = IsRefinableItem(refinableItems, item);

          return (
            <li key={item.value} className={!isRefinable ? 'facet-empty' : 'with-value'}>
              <label>
                <input
                  type="radio"
                  name={attribute}
                  data-checked={item.isRefined}
                  checked={item.isRefined}
                  onChange={() => handleClick(item.value)}
                  disabled={!isRefinable}
                />
                <span>{item.label}</span>
                <span className='ais-Menu-count'> ({item.count})</span>
              </label>
            </li>
          );
        })}
    </ul>
  );
}

function FilterWrapper(props) {
  const {label, children, kind, attribute} = props;
  const classNames = ['filter-wrapper', `filter-wrapper--${kind}`];
  return (
    <details className={classNames.join(' ')} data-attribute={attribute} data-kind={kind} open={true}>
      <summary>
        <strong>{label}</strong>
        <div className="filter-wrapper__arrow"></div>
      </summary>
      <div className='filter-wrapper__content'>
        {children}
      </div>
    </details>
  );
}

function CheckboxesFilter({label, attribute, initialFacetValues}) {
  return (
    <FilterWrapper label={label} attribute={attribute} kind='checkboxes'>
      <CustomRefinementList attribute={attribute} initialFacetValues={initialFacetValues}/>
    </FilterWrapper>
  );
}

function RadioButtonsFilter({label, attribute, initialFacetValues}) {
  return (
    <FilterWrapper label={label} attribute={attribute} kind='radio-buttons'>
      <CustomMenu attribute={attribute} initialFacetValues={initialFacetValues}/>
    </FilterWrapper>
  );
}

export default function Filter(props) {
  const {config, facetCounts} = props;
  const {
    attribute = null,
    label,
    kind,
  } = config;

  if (!attribute) {
    return null;
  }

  // Extract initial facet values for this attribute from facetCounts
  const initialFacetValues = facetCounts?.find(fc => fc.field_name === attribute)?.counts || [];

  switch (kind) {
    case 'radio':
      return <RadioButtonsFilter label={label} attribute={attribute} initialFacetValues={initialFacetValues}/>;
    case 'checkboxes':
      return <CheckboxesFilter label={label} attribute={attribute} initialFacetValues={initialFacetValues}/>;
    default:
      return null;
  }
}
